import { MouseEvent, useCallback } from 'react';

import { GAME_MODE } from '~/components/casino-shared/constants';
import { gamesByGameCodeSelector, hasDepositLoginSelector } from '~/components/casino-shared/ducks/selectors';
import useGameLauncher from '~/components/casino-shared/explore/utilities/use-game-launcher';
import enhanceImageView from '~/components/casino-shared/utilities/enhanceImage';
import getGameUrls from '~/components/casino-shared/utilities/getGameUrls';
import { createImageViewFactory } from '~/components/utilities/image-format';
import useConfiguration from '~/configuration/use-configuration';
import { BaseResponse, requestApi } from '~/services/api-new';
import { useAppDispatch } from '~/store/types';
import { showQueuedNotification } from '../../notifications/ducks/actions';
import { useCasinoSharedSelector } from '../ducks/types';
import { EnhancedGame, Game } from '../types';
import { WidgetData } from './utils';

const useLaunchNewGame = ({ type, trackGameClick }: { type: string; trackGameClick: WidgetData['trackGameClick'] }) => {
	const dispatch = useAppDispatch();
	const games = useCasinoSharedSelector(gamesByGameCodeSelector);
	const { imageFormats, imageDescriptors, license } = useConfiguration();
	const { gameLauncher } = useGameLauncher();
	const hasDepositLogin = useCasinoSharedSelector(hasDepositLoginSelector);

	const launchGame = useCallback(
		(game: EnhancedGame, event?: MouseEvent<HTMLElement, globalThis.MouseEvent>) => {
			trackGameClick({
				game,
				mode: GAME_MODE.REAL,
				position: 0
			});
			gameLauncher({
				event,
				mode: GAME_MODE.REAL,
				game,
				listName: type,
				listGameIndex: 0,
				depositLogin: hasDepositLogin
			});
		},
		[gameLauncher, hasDepositLogin, trackGameClick, type]
	);

	const playGame = useCallback(
		async (event: MouseEvent<HTMLElement, globalThis.MouseEvent>, gameCode: string) => {
			if (!gameCode) {
				dispatch(
					showQueuedNotification({
						messageKey: 'WIDGET_ERROR_GAME_NOT_FOUND',
						color: 'red',
						autoHide: true
					})
				);
				return;
			}

			if (!games[gameCode]) {
				try {
					const response = await requestApi<BaseResponse<[Game]>>({
						endpoint: `/rest/state/casino/games/v2/${gameCode}`
					});

					if (response.status === 'FAILURE' || !response.result) {
						dispatch(
							showQueuedNotification({
								messageKey: 'WIDGET_ERROR_GAME_NOT_FOUND',
								color: 'red',
								autoHide: true
							})
						);
						return;
					}

					const game = response.result[0];

					if (!game) {
						dispatch(
							showQueuedNotification({
								messageKey: 'WIDGET_ERROR_GAME_NOT_FOUND',
								color: 'red',
								autoHide: true
							})
						);
						return;
					}

					const formatEnhancer = createImageViewFactory(imageFormats, imageDescriptors);

					const enhancedGame = {
						...game,
						...getGameUrls(game, license),
						imageView: enhanceImageView(game.imageView, imageDescriptors, formatEnhancer)
					};

					launchGame(enhancedGame, event);
				} catch (err) {
					dispatch(
						showQueuedNotification({
							messageKey: 'WIDGET_ERROR_GAME_NOT_FOUND',
							color: 'red',
							autoHide: true
						})
					);
				}
				return;
			}

			launchGame(games[gameCode], event);
		},
		[dispatch, games, imageDescriptors, imageFormats, launchGame, license]
	);
	return playGame;
};

export default useLaunchNewGame;
